import { catchError, mergeMap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import { AuthControlState } from '../../internal';

export const METADATA_ENTITY_TOKEN = new StateToken<MetadataEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.METADATA_ENTITY);

export type MetadataEntityStateModel = {
  entity: FlatModels.MetadataEntityModels.Metadata | null
}

@State<MetadataEntityStateModel>({
  name: METADATA_ENTITY_TOKEN,
  defaults: {
    entity: null,
  }
})
@Injectable()
export class MetadataEntityState {
  constructor(protected store: Store,
                     protected apiControlService: ApiControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([MetadataEntityState])
  static metadata(state: MetadataEntityStateModel) {
    return state.entity;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.MetadataEntityActions.GetEntitySuccess)
  getEntitySuccess({patchState}: StateContext<MetadataEntityStateModel>,
                   {entityQueryResult}: FlatActions.MetadataEntityActions.GetEntitySuccess) {
    patchState({
      entity: entityQueryResult.entity
    });
  }

  @Action(FlatActions.MetadataEntityActions.GetEntitiesSuccess)
  getEntitiesSuccess({patchState}: StateContext<MetadataEntityStateModel>,
                     {entityQueryResults}: FlatActions.MetadataEntityActions.GetEntitiesSuccess) {
    patchState({
      entity: entityQueryResults[0].entity
    });
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.MetadataEntityActions.AddEntity)
  addEntity(_: StateContext<MetadataEntityStateModel>,
            {entity}: FlatActions.MetadataEntityActions.AddEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.MetadataEntityState.addEntity"
      );
    }

    // Add Metadata
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.METADATA_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.MetadataEntityActions.UpdateEntity)
  updateEntity(_: StateContext<MetadataEntityStateModel>,
               {entity}: FlatActions.MetadataEntityActions.UpdateEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.MetadataEntityState.updateEntity"
      );
    }

    // Update Metadata
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.METADATA_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.MetadataEntityActions.RemoveEntity)
  removeEntity(_: StateContext<MetadataEntityStateModel>,
               {entity}: FlatActions.MetadataEntityActions.RemoveEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.MetadataEntityState.removeEntity"
      );
    }

    // Remove Metadata
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.METADATA_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  //</editor-fold>

  //</editor-fold>
}
